'use client';

import { createClient } from '@/lib/supabase/client';

export type BannerType = 'info' | 'warning' | 'urgent' | 'exam' | 'timetable';

export interface NotificationBanner {
  id: string;
  title: string;
  message: string;
  type: BannerType;
  targetCohort: string;
  targetRole: 'student' | 'lecturer' | 'all';
  isActive: boolean;
  createdBy?: string;
  expiresAt?: string;
  videoUrl?: string;
  imageUrl?: string;
  createdAt: string;
}

function dbToBanner(row: any): NotificationBanner {
  return {
    id: row.id,
    title: row.title,
    message: row.message,
    type: row.type as BannerType,
    targetCohort: row.target_cohort,
    targetRole: row.target_role,
    isActive: row.is_active,
    createdBy: row.created_by || undefined,
    expiresAt: row.expires_at || undefined,
    videoUrl: row.video_url || undefined,
    imageUrl: row.image_url || undefined,
    createdAt: row.created_at,
  };
}

export const bannerService = {
  async getActive(cohort: string, role: 'student' | 'lecturer'): Promise<NotificationBanner[]> {
    const supabase = createClient();
    try {
      const now = new Date().toISOString();
      const { data, error } = await supabase
        .from('notification_banners')
        .select('*')
        .eq('is_active', true)
        .or(`expires_at.is.null,expires_at.gt.${now}`)
        .order('created_at', { ascending: false });
      if (error) return [];
      return (data || [])
        .map(dbToBanner)
        .filter(b =>
          (b.targetRole === role || b.targetRole === 'all') &&
          (b.targetCohort === 'all' || b.targetCohort === cohort)
        );
    } catch {
      return [];
    }
  },

  async getAll(): Promise<NotificationBanner[]> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('notification_banners')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) return [];
      return (data || []).map(dbToBanner);
    } catch {
      return [];
    }
  },

  async create(banner: Omit<NotificationBanner, 'id' | 'createdAt'>): Promise<NotificationBanner | null> {
    const supabase = createClient();
    try {
      const { data, error } = await supabase
        .from('notification_banners')
        .insert({
          title: banner.title,
          message: banner.message,
          type: banner.type,
          target_cohort: banner.targetCohort,
          target_role: banner.targetRole,
          is_active: banner.isActive,
          created_by: banner.createdBy || null,
          expires_at: banner.expiresAt || null,
          video_url: banner.videoUrl || null,
          image_url: banner.imageUrl || null,
        })
        .select()
        .single();
      if (error) return null;
      return data ? dbToBanner(data) : null;
    } catch {
      return null;
    }
  },

  async toggle(id: string, isActive: boolean): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('notification_banners')
        .update({ is_active: isActive })
        .eq('id', id);
      return !error;
    } catch {
      return false;
    }
  },

  async remove(id: string): Promise<boolean> {
    const supabase = createClient();
    try {
      const { error } = await supabase
        .from('notification_banners')
        .delete()
        .eq('id', id);
      return !error;
    } catch {
      return false;
    }
  },

  subscribeToChanges(onUpdate: () => void) {
    const supabase = createClient();
    const channel = supabase
      .channel('notification_banners_changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'notification_banners' }, () => {
        onUpdate();
      })
      .subscribe();
    return channel;
  },
};
